import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { AxiosRequestConfig, isCancel } from 'axios';

import * as apiRequestsHelpers from '@/helpers/api/requests';
import {
  // validateAcceptedStatusReqConf,
  validateCreatedStatusReqConf,
  // validateNoContentStatusReqConf,
  validateOKStatusReqConf,
  // validateOKn404StatusReqConf,
} from '@/helpers/fetchers';
import {
  showErrorNotification,
  showSuccessNotification,
} from '@/helpers/notifiers';
import { sendWarning } from '@/helpers/sentry';

export async function fetchGetCatalogDrivers(
  params: openapi.GetCatalogDrivers_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogDrivers_200> {
  const notificationTitle = 'Error when getting the list of all Drivers';
  return await apiRequestsHelpers
    .getCatalogDrivers(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogOffsiders(
  params: openapi.GetCatalogOffsiders_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogOffsiders_200> {
  const notificationTitle = 'Error when getting the list of all Offsiders';
  return await apiRequestsHelpers
    .getCatalogOffsiders(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogServiceCodes(
  params: openapi.GetCatalogServiceCodes_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogServiceCodes_200> {
  const notificationTitle = 'Error when getting the list of all Service Codes';
  return await apiRequestsHelpers
    .getCatalogServiceCodes(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogAttributes(
  params: openapi.GetCatalogAttributes_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogAttributes_200> {
  const notificationTitle = 'Error when getting the list of all Attributes';
  return await apiRequestsHelpers
    .getCatalogAttributes(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogGoodsTypes(
  params: openapi.GetCatalogGoodsTypes_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogGoodsTypes_200> {
  const notificationTitle = 'Error when getting the list of all Product Types';
  return await apiRequestsHelpers
    .getCatalogGoodsTypes(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogSites(
  params: openapi.GetCatalogSites_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogSites_200> {
  const notificationTitle = 'Error when getting the list of all Sites';
  return await apiRequestsHelpers
    .getCatalogSites(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogServiceGroups(
  params: openapi.GetCatalogServiceGroups_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogServiceGroups_200> {
  const notificationTitle = 'Error when getting the list of all Service Groups';
  return await apiRequestsHelpers
    .getCatalogServiceGroups(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogTechnicians(
  params: openapi.GetCatalogTechnicians_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogTechnicians_200> {
  const notificationTitle = 'Error when getting the list of all Technicians';
  return await apiRequestsHelpers
    .getCatalogTechnicians(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchPostCatalogTechnicians(
  params: ReadonlyDeep<openapi.PostCatalogTechnicians_Query>,
  data: ReadonlyDeep<openapi.PostCatalogTechnicians_Body>,
  requestConfig: Readonly<AxiosRequestConfig> = {},
): Promise<openapi.PostCatalogTechnicians_201> {
  return await apiRequestsHelpers
    .postCatalogTechnicians(params, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({
        text: 'Technician successfully created',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error creating Technician' });
      }
      throw err;
    });
}

export async function fetchPutCatalogTechniciansByid(
  params: openapi.PutCatalogTechniciansById_Query,
  path: openapi.PutCatalogTechniciansById_Path,
  data: openapi.PutCatalogTechniciansById_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PutCatalogTechniciansById_200> {
  return await apiRequestsHelpers
    .putCatalogTechniciansByid(params, path, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({
        text: 'Existing technician successfully updated',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error updating existing Technician' });
      }
      throw err;
    });
}

export async function fetchPatchCatalogTechniciansByid(
  params: openapi.PatchCatalogTechniciansById_Query,
  path: openapi.PatchCatalogTechniciansById_Path,
  data: openapi.PatchCatalogTechniciansById_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PatchCatalogTechniciansById_200> {
  return await apiRequestsHelpers
    .patchCatalogTechniciansByid(params, path, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({
        text: 'Existing technician successfully partially updated',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({
          title: 'Error partially updating existing Technician',
        });
      }
      throw err;
    });
}

export async function fetchGetCatalogBases(
  params: openapi.GetCatalogBases_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogBases_200> {
  const notificationTitle = 'Error when getting the list of Bases';
  return await apiRequestsHelpers
    .getCatalogBases(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogZones(
  params: openapi.GetCatalogZones_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogZones_200> {
  const notificationTitle = 'Error when getting the list of all Zones';
  return await apiRequestsHelpers
    .getCatalogZones(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogServiceAreas(
  params: openapi.GetCatalogServiceAreas_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogServiceAreas_200> {
  const notificationTitle = 'Error when getting the list of all Service Areas';
  return await apiRequestsHelpers
    .getCatalogServiceAreas(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogFleets(
  params: openapi.GetCatalogFleets_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogFleets_200> {
  const notificationTitle = 'Error when getting the list of all Fleet types';
  return await apiRequestsHelpers
    .getCatalogFleets(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogDriverTypes(
  params: openapi.GetCatalogDriverTypes_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogDriverTypes_200> {
  const notificationTitle = 'Error when getting the list of all Driver types';
  return await apiRequestsHelpers
    .getCatalogDriverTypes(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchGetCatalogAll(
  params: openapi.GetCatalogAll_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetCatalogAll_200> {
  const notificationTitle = 'Error when getting all catalog data';
  return await apiRequestsHelpers
    .getCatalogAll(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}
